import { useDeferredValue, useMemo, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import { MasterCrudTabShell } from '~/pages/AdminOcctoMaster/shell/MasterCrudTabShell'
import { useMasterCrudTab } from '~/pages/AdminOcctoMaster/shell/useMasterCrudTab'
import { CreateSubmitterMasterModal } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/components/CreateSubmitterMasterModal'
import { DeleteSubmitterMasterDialog } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/components/DeleteSubmitterMasterDialog'
import { EditSubmitterMasterModal } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/components/EditSubmitterMasterModal'
import { SubmitterMasterTable } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/components/SubmitterMasterTable'
import {
  type SubmitterMasterRow,
  convertListSubmitterMastersResponse,
  useListSubmitterMasters,
} from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useListSubmitterMasters'
import { submitterMasterCsvColumns } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/models/csvColumns'
import { ExpiredFilterToggle } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredFilterToggle'
import { MasterFilterToolbar } from '~/pages/AdminOcctoMaster/tabs/_common/components/MasterFilterToolbar'

function filterRows(
  rows: SubmitterMasterRow[],
  areaCode: AreaCode,
  searchText: string,
): SubmitterMasterRow[] {
  const trimmed = searchText.trim().toLowerCase()
  return rows
    .filter((row) => {
      if (areaCode !== AreaCode.UNSPECIFIED && row.areaCode !== areaCode) {
        return false
      }
      if (trimmed === '') {
        return true
      }
      return (
        row.submitterCode.toLowerCase().includes(trimmed) ||
        row.submitterName.toLowerCase().includes(trimmed)
      )
    })
    .slice()
    .sort((a, b) => a.submitterCode.localeCompare(b.submitterCode))
}

export const SubmitterMasterTab = (): React.ReactElement => {
  const { t } = useTranslation()
  const { data, isLoading, isError, error } = useListSubmitterMasters()

  const [selectedAreaCode, setSelectedAreaCode] = useState<AreaCode>(
    AreaCode.UNSPECIFIED,
  )
  const [searchText, setSearchText] = useState('')
  const deferredSearchText = useDeferredValue(searchText)

  const rows = useMemo(
    () => (data !== undefined ? convertListSubmitterMastersResponse(data) : []),
    [data],
  )
  const filteredRows = useMemo(
    () => filterRows(rows, selectedAreaCode, deferredSearchText),
    [rows, selectedAreaCode, deferredSearchText],
  )

  const subject = t('page:adminOcctoMaster.common.subject.submitter')
  const tab = useMasterCrudTab<SubmitterMasterRow>({
    data,
    isLoading,
    isError,
    error,
    errorKeyPrefix: 'page:adminOcctoMaster.submitterMaster.serverError',
    filteredRows,
    csv: { columns: submitterMasterCsvColumns(), fileBase: 'submitter' },
    expiry: {
      getPeriod: (row) => ({
        validFrom: row.validFrom,
        validTo: row.validTo,
      }),
      subject,
    },
  })

  const deleteRow = tab.modalState.kind === 'delete' ? tab.modalState.row : null

  return (
    <MasterCrudTabShell
      createModal={
        // 条件 mount (閉じたら form ごと破棄): 常時 mount + isOpen 切替だと
        // 保存成功時の reset() 直後に mount 中の field array 行入力 (構成 BG
        // リスト) が RHF の再 register で直前の値を書き戻し、次回 open で行が
        // 残留する (#6072、詳細は DemandProcurementPlanMaster/index.tsx)。
        tab.modalState.kind === 'create' ? (
          <CreateSubmitterMasterModal isOpen onClose={tab.closeModal} />
        ) : null
      }
      csvExportDisabledReason={tab.csvExportDisabledReason}
      deleteDialog={
        deleteRow !== null ? (
          <DeleteSubmitterMasterDialog
            row={deleteRow}
            errorIsRetriable={tab.deleteError?.retriable}
            errorMessage={tab.deleteError?.message}
            isOpen={tab.modalState.kind === 'delete'}
            onClose={tab.closeModal}
            onError={tab.setDeleteError}
          />
        ) : null
      }
      editModal={
        tab.modalState.kind === 'edit' ? (
          <EditSubmitterMasterModal
            row={tab.modalState.row}
            isOpen
            onClose={tab.closeModal}
          />
        ) : null
      }
      filterToolbar={
        <MasterFilterToolbar
          onSearchTextChange={setSearchText}
          onSelectArea={setSelectedAreaCode}
          searchPlaceholder={t(
            'page:adminOcctoMaster.submitterMaster.toolbar.searchPlaceholder',
          )}
          searchText={searchText}
          selectedAreaCode={selectedAreaCode}
          trailing={
            <ExpiredFilterToggle
              expiredCount={tab.expiredCount}
              includeExpired={tab.includeExpired}
              onChange={tab.setIncludeExpired}
              subject={subject}
            />
          }
        />
      }
      onAddIndividual={tab.openCreate}
      onCsvExport={tab.csvExportEnabled ? tab.handleCsvExport : undefined}
      showInitialLoading={tab.showInitialLoading}
      table={
        <SubmitterMasterTable
          emptyLabel={tab.emptyLabel}
          errorLabel={tab.errorLabel}
          isLoading={isLoading}
          onDelete={tab.openDelete}
          onEdit={tab.openEdit}
          rows={tab.visibleRows}
          today={tab.today}
        />
      }
    />
  )
}
